export type RevenuePipelineStage =
  | "pending"
  | "past_cancellation_window"
  | "awaiting_payout"
  | "paid_out";

export type OperatingCostCategory =
  | "firebase"
  | "storage"
  | "stripe_fees"
  | "apple_fees"
  | "email_sms"
  | "subscriptions"
  | "other";

export type FounderFinanceRevenuePipelineRow = {
  bookingId: string;
  marketplaceTransactionId: string | null;
  sourceType: string | null;
  sourceId: string | null;
  payableUnitId: string | null;
  payableUnitTitle: string | null;
  buyerUserId: string | null;
  providerUserId: string | null;
  offeringType: string | null;
  offeringId: string | null;
  bookingStatus: string | null;
  cancellationWindowEndsAt: string | null;
  scheduledStartAt: string | null;
  scheduledEndAt: string | null;
  completedAt: string | null;
  payoutStatus: string | null;
  financeStatus: FounderFinanceBookingMoneyStatus;
  customerPaidAmount: number;
  grossAmount: number;
  stripeFeeAmount: number;
  stripeProcessingFeeAmount: number | null;
  appleFeeAmount: number;
  platformFeeAmount: number;
  coachGrossEarningsAmount: number;
  providerPayoutAmount: number;
  coachReleasedAmount: number;
  talentLiabilityAmount: number;
  releasedTalentAmount: number;
  netPlatformRevenue: number;
  fundsStatus: string | null;
  transferStatus: string | null;
  transferId: string | null;
  connectedAccountId: string | null;
  paymentIntentId: string | null;
  stripePayoutId: string | null;
  stripePayoutStatus: string | null;
  stripePayoutCreatedAt: string | null;
  stripePayoutArrivalAt: string | null;
  releaseAttemptCount: number;
  releaseLeaseExpiresAt: string | null;
  failureReason: string | null;
  releaseDate: string | null;
  payoutDate: string | null;
  currency: string;
  createdAt: string | null;
  updatedAt: string | null;
  stage: RevenuePipelineStage;
};

export type FounderFinanceBookingMoneyStatus =
  | "held"
  | "eligible"
  | "released"
  | "transferred"
  | "paid_out"
  | "refunded"
  | "canceled";

export type FounderFinanceOperatingCostRow = {
  id: string;
  monthKey: string;
  category: OperatingCostCategory;
  estimatedAmount: number;
  actualAmount: number | null;
  notes: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
};

export type FounderFinanceStageSummary = {
  stage: RevenuePipelineStage;
  bookingCount: number;
  grossAmount: number;
  stripeFeeAmount: number;
  appleFeeAmount: number;
  platformFeeAmount: number;
  providerPayoutAmount: number;
  netPlatformRevenue: number;
  weightedNetPlatformRevenue: number;
};

export type FounderFinanceWindowSummary = {
  label: string;
  startsAt: string;
  endsAt: string;
  bookingCount: number;
  projectedRevenue: number;
  projectedCost: number;
  projectedNet: number;
  realizedRevenue: number;
};

export type FounderFinanceOperatingCostMonth = {
  monthKey: string;
  estimatedAmount: number;
  actualAmount: number;
  forecastAmount: number;
  rows: FounderFinanceOperatingCostRow[];
};

export type MarketplaceHealthSignal = "healthy" | "warning" | "critical";

export type MarketplaceHealthMetric = {
  id: string;
  title: string;
  value: number | string | null;
  valueKind: "money_cents" | "count" | "duration_hours" | "date" | "text";
  signal: MarketplaceHealthSignal;
  detail: string;
};

export type MarketplaceHealthWarning = {
  kind: string;
  signal: MarketplaceHealthSignal;
  title: string;
  detail: string;
  bookingId: string | null;
  marketplaceTransactionId: string | null;
  ageHours: number | null;
  amount: number | null;
};

export type MarketplaceHealthSummary = {
  signal: MarketplaceHealthSignal;
  headline: string;
  generatedAt: string;
  heldFunds: number;
  releasedFunds: number;
  outstandingLiability: number;
  pendingReleases: number;
  releaseFailures: number;
  transferFailures: number;
  pendingStripeTransfers: number;
  oldestHeldBooking: string | null;
  oldestHeldBookingAt: string | null;
  oldestEligibleRelease: string | null;
  oldestEligibleReleaseAt: string | null;
  averageReleaseTimeHours: number | null;
  averageTimeHeldHours: number | null;
  platformRevenue: number;
  coachLiability: number;
  warnings: MarketplaceHealthWarning[];
  metrics: MarketplaceHealthMetric[];
};

export type MarketplaceReleaseQueueStatus =
  | "held"
  | "eligible"
  | "releasing"
  | "transferred"
  | "paid_out"
  | "failed"
  | "canceled"
  | "refunded";

export type MarketplaceReleaseQueueEntry = {
  id: string;
  status: MarketplaceReleaseQueueStatus;
  signal: MarketplaceHealthSignal;
  bookingId: string;
  sourceType: string | null;
  payableUnitId: string | null;
  payableUnitTitle: string | null;
  marketplaceTransactionId: string | null;
  coachUserId: string | null;
  buyerUserId: string | null;
  amount: number;
  heldSince: string | null;
  eligibleSince: string | null;
  nextSchedulerAttemptAt: string | null;
  transferId: string | null;
  connectedAccountId: string | null;
  releaseAttempts: number;
  failureReason: string | null;
  fundsStatus: string | null;
  transferStatus: string | null;
  sortAt: string | null;
};

export type FounderKpiId =
  | "gmv_today"
  | "platform_revenue_today"
  | "bookings_today"
  | "new_paying_dancers"
  | "new_paying_coaches"
  | "new_studios"
  | "average_booking_value"
  | "revenue_per_active_dancer"
  | "repeat_booking_rate"
  | "projected_december_31_revenue";

export type FounderKpiValueFormat = "money_cents" | "number" | "percent";
export type FounderKpiTrendDirection = "up" | "down" | "flat" | "none";
export type FounderKpiState = "ready" | "empty" | "not_enough_data" | "error";

export type FounderKpiCard = {
  id: FounderKpiId;
  title: string;
  valueFormat: FounderKpiValueFormat;
  currentValue: number | null;
  priorMonthValue: number | null;
  priorMonthLabel: string;
  growthRate: number | null;
  growthLabel: string;
  direction: FounderKpiTrendDirection;
  state: FounderKpiState;
  detail: string;
  emptyMessage: string | null;
};

export type FounderRevenueGoalTracker = {
  targetPlatformRevenue: number;
  deadline: string;
  currentYtdPlatformRevenue: number;
  remainingRevenueNeeded: number;
  requiredDailyAverage: number | null;
  projectedRevenueByDeadline: number | null;
  runRateWindowDays: number | null;
  runRateDailyAverage: number | null;
  state: "ready" | "not_enough_data";
  detail: string;
};

export type FounderKpiSummary = {
  periodStartedAt: string;
  periodEndedAt: string;
  todayStartedAt: string;
  todayEndedAt: string;
  priorMonthStartedAt: string;
  priorMonthEndedAt: string;
  cards: FounderKpiCard[];
  goalTracker: FounderRevenueGoalTracker;
  unavailable: string[];
};

export type FounderFinanceDashboardData = {
  founderKpis: FounderKpiSummary;
  currentMonthSummary: FounderFinanceWindowSummary;
  next30DaysForecast: FounderFinanceWindowSummary;
  next90DaysForecast: FounderFinanceWindowSummary;
  reconciliationSummary: {
    heldFunds: number;
    releasedFunds: number;
    outstandingLiability: number;
    platformRevenue: number;
    stripeFees: number;
    stripeFeesUnavailableCount: number;
    coachEarnings: number;
  };
  marketplaceHealth: MarketplaceHealthSummary;
  marketplaceReleaseQueue: MarketplaceReleaseQueueEntry[];
  bookingReconciliationRows: FounderFinanceRevenuePipelineRow[];
  revenuePipelineByStage: Record<RevenuePipelineStage, FounderFinanceStageSummary>;
  operatingCostsByMonth: FounderFinanceOperatingCostMonth[];
  drilldowns: {
    currentMonthSummary: FounderFinanceRevenuePipelineRow[];
    next30DaysForecast: FounderFinanceRevenuePipelineRow[];
    next90DaysForecast: FounderFinanceRevenuePipelineRow[];
    revenuePipelineByStage: Record<RevenuePipelineStage, FounderFinanceRevenuePipelineRow[]>;
    operatingCostsByMonth: Record<string, FounderFinanceOperatingCostRow[]>;
  };
  fetchedAt: string;
};

export type UpsertFounderFinanceOperatingCostInput = {
  monthKey: string;
  category: OperatingCostCategory;
  estimatedAmount: number;
  actualAmount: number | null;
  notes: string | null;
};

export const PIPELINE_STAGES: RevenuePipelineStage[] = [
  "pending",
  "past_cancellation_window",
  "awaiting_payout",
  "paid_out"
];

export const COST_CATEGORIES: Array<{ id: OperatingCostCategory; label: string }> = [
  { id: "firebase", label: "Firebase" },
  { id: "storage", label: "Storage" },
  { id: "stripe_fees", label: "Stripe fees" },
  { id: "apple_fees", label: "Apple fees" },
  { id: "email_sms", label: "Email/SMS costs" },
  { id: "subscriptions", label: "Third-party subscriptions" },
  { id: "other", label: "Other" }
];
